import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { ForceUpdateGate } from "@/components/ForceUpdateGate";
import { toast } from "sonner";

import { ErrorBoundary } from "@/components/ErrorBoundary";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import CalendarPage from "@/pages/CalendarPage";
import SettingsPage from "@/pages/SettingsPage";
import RequestsPage from "@/pages/RequestsPage";
import UsersPage from "@/pages/UsersPage";
import RepartiPage from "@/pages/RepartiPage";
import InstructionsPage from "@/pages/InstructionsPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import AbbonamentoPage from "@/pages/AbbonamentoPage";
import DipendentiBasePage from "@/pages/DipendentiBasePage";
import { isIOSApp } from "@/utils/platform";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ 
    nome_azienda: "La Tua Azienda", 
    logo_base64: null,
    giorno_inizio_settimana: 1,
    giorno_fine_settimana: 5,
    limite_richieste: "tutto_anno",
    limite_mesi: 3,
    chiusura_infrasettimanale_attiva: false,
    chiusura_infrasettimanale_giorno: null,
    chiusura_infrasettimanale_dalle: null,
    chiusura_infrasettimanale_alle: null,
    tema_colore: "blue",
    piano: "base"
  });

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          // Load settings for authenticated user's company
          const settingsRes = await axios.get(`${API}/settings/company`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSettings(settingsRes.data);
          // Apply theme
          document.documentElement.setAttribute('data-theme', settingsRes.data.tema_colore || 'blue');
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
        }
      } else {
        // Load settings for public pages (login/register)
        try {
          const settingsRes = await axios.get(`${API}/settings`);
          setSettings(settingsRes.data);
          document.documentElement.setAttribute('data-theme', settingsRes.data.tema_colore || 'blue');
        } catch (error) {
          console.error("Failed to load settings");
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    // Load settings for authenticated user's company
    const settingsRes = await axios.get(`${API}/settings/company`, {
      headers: { Authorization: `Bearer ${newToken}` }
    });
    setSettings(settingsRes.data);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  // Multi-azienda: passa a un account collegato senza logout
  const switchAccount = async (aziendaId) => {
    const currentToken = localStorage.getItem("token");
    const response = await axios.post(`${API}/auth/switch-account`, { azienda_id: aziendaId }, {
      headers: { Authorization: `Bearer ${currentToken}` }
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    sessionStorage.removeItem("review_prompt_shown");
    const settingsRes = await axios.get(`${API}/settings/company`, { headers: { Authorization: `Bearer ${newToken}` } });
    setSettings(settingsRes.data);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const refreshSettings = async () => {
    try {
      const currentToken = localStorage.getItem("token");
      const endpoint = currentToken ? `${API}/settings/company` : `${API}/settings`;
      const config = currentToken ? { headers: { Authorization: `Bearer ${currentToken}` } } : {};
      const settingsRes = await axios.get(endpoint, config);
      setSettings(settingsRes.data);
      // Apply theme
      document.documentElement.setAttribute('data-theme', settingsRes.data.tema_colore || 'blue');
    } catch (error) {
      console.error("Failed to refresh settings");
    }
  };

  const completeOnboarding = async () => {
    try {
      await axios.put(`${API}/auth/complete-onboarding`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(prev => ({ ...prev, onboarding_completato: true }));
    } catch (error) {
      console.error("Failed to complete onboarding");
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Failed to refresh user");
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, switchAccount, loading, settings, refreshSettings, completeOnboarding, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Routes hidden on Base plan (Settings, Users) — redirect to Abbonamento for upgrade
const PlanGatedRoute = ({ children }) => {
  const { user, loading, settings } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  const isBasePlan = (settings?.piano || "base") === "base";
  // On iOS: do NOT redirect to the subscription page (Apple Guideline 3.1.1).
  // The user stays on whatever page they're on; the AbbonamentoPage itself
  // displays a read-only "Multi-platform service" notice on iOS.
  if (isBasePlan && !isIOSApp()) {
    return <Navigate to="/abbonamento" replace />;
  }
  
  return children;
};

// Settings is special: titolari during the free trial keep access so they can
// complete the onboarding wizard even when their plan is still flagged as "base".
const SettingsRoute = ({ children }) => {
  const { user, loading, settings } = useAuth();
  const [trialActive, setTrialActive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      if (!user || user.ruolo !== "titolare") {
        if (!cancelled) setTrialActive(false);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          const inTrial = !!res.data?.is_trial && !res.data?.trial_expired;
          const paid = !!res.data?.payment_completed;
          setTrialActive(inTrial || paid);
        }
      } catch {
        if (!cancelled) setTrialActive(false);
      }
    };
    fetchStatus();
    return () => { cancelled = true; };
  }, [user]);

  if (loading || trialActive === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isBasePlan = (settings?.piano || "base") === "base";
  // Block only when on Base plan AND trial is over AND no payment completed.
  // Skip the block entirely on iOS (Apple Guideline 3.1.1 — no external
  // payment funnel may be triggered by the iOS app).
  if (isBasePlan && !trialActive && !isIOSApp()) {
    return <Navigate to="/abbonamento" replace />;
  }

  return children;
};

// Dashboard route: dipendenti are redirected to /requests (their new "Home")
const DashboardRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.ruolo === "dipendente") {
    return <Navigate to="/requests" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
          <Route path="/dashboard" element={<DashboardRoute><DashboardPage /></DashboardRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
          <Route path="/requests" element={<ProtectedRoute><RequestsPage /></ProtectedRoute>} />
          <Route path="/settings" element={<SettingsRoute><SettingsPage /></SettingsRoute>} />
          <Route path="/abbonamento" element={<ProtectedRoute><AbbonamentoPage /></ProtectedRoute>} />
          <Route path="/dipendenti" element={<ProtectedRoute><DipendentiBasePage /></ProtectedRoute>} />
          <Route path="/users" element={<PlanGatedRoute><UsersPage /></PlanGatedRoute>} />
          <Route path="/reparti" element={<PlanGatedRoute><RepartiPage /></PlanGatedRoute>} />
          <Route path="/instructions" element={<ProtectedRoute><InstructionsPage /></ProtectedRoute>} />
          <Route path="/super-admin" element={<SuperAdminPage />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
      <ForceUpdateGate />
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
